// Fiyat ve vergi: KDV önce (fiyatın anlamını belirler), ana fiyatlar her zaman, ek fiyatlar ve ÖTV istenince.
import React, { useEffect, useState } from 'react';
import {
  Box, Chip, FormControl, FormHelperText, InputAdornment, InputLabel, MenuItem, Select, Stack, TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { StockCardForm } from '../useStockCardForm';
import { AddLink, GroupLabel, MoneyField, SectionTitle, toNumberOrEmpty } from './fields';

const COMMON_VAT_RATES = [0, 1, 10, 20];

const hasValue = (v: unknown) => v !== '' && v !== null && v !== undefined && v !== 0 && v !== '0';

export const PriceTaxSection: React.FC<{ f: StockCardForm }> = ({ f }) => {
  const { t } = useTranslation('accounting');

  const extraPricesFilled = [
    f.purchasePrice2, f.purchasePrice3, f.salePrice2, f.salePrice3,
    f.purchaseVendor2, f.purchaseVendor3, f.saleCustomer2, f.saleCustomer3,
  ].some(hasValue);
  const partiesFilled = hasValue(f.purchaseVendor) || hasValue(f.saleCustomer);
  const sctFilled = hasValue(f.sctRate);

  const [showExtraPrices, setShowExtraPrices] = useState(extraPricesFilled);
  const [showParties, setShowParties] = useState(partiesFilled);
  const [showSct, setShowSct] = useState(sctFilled);

  // Düzenlemede değerler sonradan yüklenir; dolu alanlar açık gelsin.
  useEffect(() => { if (extraPricesFilled) setShowExtraPrices(true); }, [extraPricesFilled]);
  useEffect(() => { if (partiesFilled) setShowParties(true); }, [partiesFilled]);
  useEffect(() => { if (sctFilled) setShowSct(true); }, [sctFilled]);

  const saleMissing = typeof f.salePrice === 'string' ? f.salePrice === '' : f.salePrice === 0;

  return (
    <Box>
      <SectionTitle
        title={t('stock.addModal.layout.sections.price', 'Fiyat ve vergi')}
        hint={t('stock.addModal.layout.priceHint', 'Önce KDV: girdiğiniz fiyatların KDV dahil mi hariç mi olduğunu belirler.')}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        <Box>
          <TextField
            id="stock-card-vat-rate"
            fullWidth
            required
            size="small"
            type="number"
            label={t('stock.addModal.fields.vatRate')}
            value={f.vatRate}
            onChange={(e) => f.setVatRate(toNumberOrEmpty(e.target.value))}
            error={f.vatRate === ''}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
            {COMMON_VAT_RATES.map(rate => (
              <Chip
                key={rate}
                size="small"
                label={`%${rate}`}
                color={f.vatRate === rate ? 'primary' : 'default'}
                variant={f.vatRate === rate ? 'filled' : 'outlined'}
                onClick={() => f.setVatRate(rate)}
              />
            ))}
          </Stack>
        </Box>
        <FormControl fullWidth size="small" required error={f.vatStatus === ''}>
          <InputLabel id="stock-card-vat-status-label">{t('stock.addModal.fields.vatStatusRequired')}</InputLabel>
          <Select
            labelId="stock-card-vat-status-label"
            id="stock-card-vat-status"
            value={f.vatStatus}
            label={t('stock.addModal.fields.vatStatusRequired')}
            onChange={(e) => f.setVatStatus(String(e.target.value))}
          >
            <MenuItem value="included">{t('stock.quickAdd.vatIncluded')}</MenuItem>
            <MenuItem value="excluded">{t('stock.quickAdd.vatExcluded')}</MenuItem>
          </Select>
          <FormHelperText>
            {t('stock.addModal.layout.vatStatusHint', 'Aşağıdaki tüm fiyatlar için geçerlidir')}
          </FormHelperText>
        </FormControl>
      </Box>

      <GroupLabel>{t('stock.addModal.layout.mainPrices', 'Ana fiyatlar')}</GroupLabel>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        <MoneyField
          f={f}
          id="stock-card-sale-price"
          label={t('stock.addModal.layout.salePrice', 'Satış fiyatı')}
          value={f.salePrice}
          onChange={f.setSalePrice}
          currency={f.saleCurrency}
          onCurrencyChange={f.setSaleCurrency}
          required
          error={saleMissing}
        />
        <MoneyField
          f={f}
          id="stock-card-purchase-price"
          label={t('stock.addModal.layout.purchasePrice', 'Alış fiyatı')}
          value={f.purchasePrice}
          onChange={f.setPurchasePrice}
          currency={f.purchaseCurrency}
          onCurrencyChange={f.setPurchaseCurrency}
        />
        {showParties && (
          <>
            <TextField
              id="stock-card-sale-customer"
              size="small"
              fullWidth
              label={t('stock.addModal.fields.customerGroup1')}
              placeholder={t('stock.addModal.fields.customerNamePlaceholder')}
              value={f.saleCustomer}
              onChange={(e) => f.setSaleCustomer(e.target.value)}
            />
            <TextField
              id="stock-card-purchase-vendor"
              size="small"
              fullWidth
              label={t('stock.addModal.fields.supplier1')}
              placeholder={t('stock.addModal.fields.supplierNamePlaceholder')}
              value={f.purchaseVendor}
              onChange={(e) => f.setPurchaseVendor(e.target.value)}
            />
          </>
        )}
      </Box>
      {!showParties && (
        <AddLink label={t('stock.addModal.layout.addParties', 'Müşteri grubu / tedarikçi adı ekle')} onClick={() => setShowParties(true)} />
      )}

      <Box sx={{ mt: 1.5 }}>
        <MoneyField
          f={f}
          id="stock-card-list-price"
          label={t('stock.addModal.fields.listPrice')}
          value={f.listPrice}
          onChange={f.setListPrice}
          currency={f.listCurrency}
          onCurrencyChange={f.setListCurrency}
          helperText={t('stock.addModal.fields.listPriceHint')}
        />
      </Box>

      {showExtraPrices ? (
        <>
          <GroupLabel>{t('stock.addModal.layout.extraPrices', 'Ek fiyatlar')}</GroupLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            <MoneyField f={f} id="stock-card-sale-price-2" label={t('stock.addModal.fields.salePrice2')}
              value={f.salePrice2} onChange={f.setSalePrice2} currency={f.saleCurrency2} onCurrencyChange={f.setSaleCurrency2} />
            <TextField id="stock-card-sale-customer-2" size="small" fullWidth label={t('stock.addModal.fields.customerGroup2')}
              placeholder={t('stock.addModal.fields.customerNamePlaceholder')} value={f.saleCustomer2}
              onChange={(e) => f.setSaleCustomer2(e.target.value)} />
            <MoneyField f={f} id="stock-card-sale-price-3" label={t('stock.addModal.fields.salePrice3')}
              value={f.salePrice3} onChange={f.setSalePrice3} currency={f.saleCurrency3} onCurrencyChange={f.setSaleCurrency3} />
            <TextField id="stock-card-sale-customer-3" size="small" fullWidth label={t('stock.addModal.fields.customerGroup3')}
              placeholder={t('stock.addModal.fields.customerNamePlaceholder')} value={f.saleCustomer3}
              onChange={(e) => f.setSaleCustomer3(e.target.value)} />
            <MoneyField f={f} id="stock-card-purchase-price-2" label={t('stock.addModal.fields.purchasePrice2')}
              value={f.purchasePrice2} onChange={f.setPurchasePrice2} currency={f.purchaseCurrency2} onCurrencyChange={f.setPurchaseCurrency2} />
            <TextField id="stock-card-purchase-vendor-2" size="small" fullWidth label={t('stock.addModal.fields.supplier2')}
              placeholder={t('stock.addModal.fields.supplierNamePlaceholder')} value={f.purchaseVendor2}
              onChange={(e) => f.setPurchaseVendor2(e.target.value)} />
            <MoneyField f={f} id="stock-card-purchase-price-3" label={t('stock.addModal.fields.purchasePrice3')}
              value={f.purchasePrice3} onChange={f.setPurchasePrice3} currency={f.purchaseCurrency3} onCurrencyChange={f.setPurchaseCurrency3} />
            <TextField id="stock-card-purchase-vendor-3" size="small" fullWidth label={t('stock.addModal.fields.supplier3')}
              placeholder={t('stock.addModal.fields.supplierNamePlaceholder')} value={f.purchaseVendor3}
              onChange={(e) => f.setPurchaseVendor3(e.target.value)} />
          </Box>
        </>
      ) : (
        <AddLink label={t('stock.addModal.layout.addExtraPrices', '2. ve 3. satış / alış fiyatı ekle')} onClick={() => setShowExtraPrices(true)} />
      )}

      {showSct ? (
        <>
          <GroupLabel>{t('stock.addModal.layout.sct', 'ÖTV')}</GroupLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
            <TextField
              id="stock-card-sct-rate"
              fullWidth
              size="small"
              type="number"
              label={t('stock.addModal.fields.sctRate')}
              value={f.sctRate}
              onChange={(e) => f.setSctRate(toNumberOrEmpty(e.target.value))}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
            <FormControl fullWidth size="small">
              <InputLabel id="stock-card-sct-status-label">{t('stock.addModal.fields.sctStatus')}</InputLabel>
              <Select
                labelId="stock-card-sct-status-label"
                id="stock-card-sct-status"
                value={f.sctStatus}
                label={t('stock.addModal.fields.sctStatus')}
                onChange={(e) => f.setSctStatus(String(e.target.value))}
              >
                <MenuItem value="included">{t('stock.addModal.fields.sctIncluded')}</MenuItem>
                <MenuItem value="excluded">{t('stock.addModal.fields.sctExcluded')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </>
      ) : (
        <AddLink label={t('stock.addModal.layout.addSct', 'ÖTV ekle')} onClick={() => setShowSct(true)} />
      )}
    </Box>
  );
};
